import React, { useRef, useState } from "react";
import toast from "react-hot-toast";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { Button, CmsField, CmsPageError, Modal } from "@/components";
import { ApiError, calendarService } from "@/services";
import type { CalendarSelectionRange } from "@/components";
import {
  buildPublicBookingSuccessWhatsAppMessage,
  formatCalendarRangeLabel,
  generateWhatsAppLink,
  isValidWhatsAppNumber,
} from "@/utils";
import "./CalendarBookingModal.scss";

const SUCCESS_MESSAGE =
  "¡Horario pre-reservado! La sala revisará tu solicitud. En breve nos pondremos en contacto para confirmarla.";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as
  | string
  | undefined;

type ModalStep = "form" | "success" | "submitting";

export interface CalendarBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantSlug: string;
  roomId: string | null;
  range: CalendarSelectionRange;
  spaceWhatsapp?: string;
  onSuccess?: () => void;
}

/**
 * Modal for submitting a public calendar booking request with contact details
 */
export const CalendarBookingModal: React.FC<CalendarBookingModalProps> = ({
  isOpen,
  onClose,
  tenantSlug,
  roomId,
  range,
  spaceWhatsapp,
  onSuccess,
}) => {
  const turnstileRef = useRef<TurnstileInstance>(null);
  const [step, setStep] = useState<ModalStep>("form");
  const [contactName, setContactName] = useState("");
  const [contactWhatsapp, setContactWhatsapp] = useState("");
  const [website, setWebsite] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [whatsappError, setWhatsappError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const rangeLabel = formatCalendarRangeLabel(range.start, range.end);
  const spaceWhatsappLink = spaceWhatsapp?.trim()
    ? generateWhatsAppLink(
        spaceWhatsapp,
        buildPublicBookingSuccessWhatsAppMessage(range.start, range.end, contactName),
      )
    : null;

  const resetForm = () => {
    setStep("form");
    setContactName("");
    setContactWhatsapp("");
    setWebsite("");
    setTurnstileToken(null);
    setNameError(null);
    setWhatsappError(null);
    setFormError(null);
    turnstileRef.current?.reset();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSuccess = () => {
    setStep("success");
    onSuccess?.();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (website.trim()) {
      handleSuccess();
      return;
    }

    const trimmedName = contactName.trim();
    let hasError = false;

    if (!trimmedName) {
      setNameError("Ingresá tu nombre.");
      hasError = true;
    } else {
      setNameError(null);
    }

    if (!isValidWhatsAppNumber(contactWhatsapp)) {
      setWhatsappError("Ingresá un WhatsApp válido (8 a 15 dígitos).");
      hasError = true;
    } else {
      setWhatsappError(null);
    }

    if (!roomId) {
      setFormError("No hay sala seleccionada para reservar.");
      return;
    }

    if (!turnstileToken) {
      setFormError("Completá la verificación de seguridad antes de enviar.");
      return;
    }

    if (hasError) {
      return;
    }

    setFormError(null);
    setStep("submitting");

    try {
      await calendarService.createPublicBookingRequest(tenantSlug, {
        contactName: trimmedName,
        contactWhatsapp: contactWhatsapp.trim(),
        roomId,
        startsAt: range.start.toISOString(),
        endsAt: range.end.toISOString(),
        turnstileToken,
        website,
      });
      handleSuccess();
    } catch (err) {
      turnstileRef.current?.reset();
      setTurnstileToken(null);
      setStep("form");

      if (err instanceof ApiError && err.status === 429) {
        toast.error(
          err.responseData?.message ??
            "Alcanzaste el límite de reservas por hoy.",
        );
        return;
      }

      setFormError(
        err instanceof ApiError
          ? (err.responseData?.message ?? err.message)
          : "Error al enviar la solicitud. Intentá de nuevo.",
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        step === "success" ? "Solicitud enviada" : "Reservar horario"
      }
    >
      {step === "success" ? (
        <div className="calendar-booking-modal__success">
          <p>{SUCCESS_MESSAGE}</p>
          <div className="calendar-booking-modal__actions">
            <Button
              variant="secondary"
              appearance="linear"
              actionTrackSufix="tenant_calendar_booking_close"
              onClick={handleClose}
            >
              Cerrar
            </Button>
            {spaceWhatsappLink && (
              <Button
                href={spaceWhatsappLink}
                variant="primary"
                actionTrackSufix="tenant_calendar_booking_wpp_space"
              >
                Contactar a la sala
              </Button>
            )}
          </div>
        </div>
      ) : (
        <form
          className="calendar-booking-modal__form"
          onSubmit={(event) => void handleSubmit(event)}
        >
          <p className="calendar-booking-modal__range">{rangeLabel}</p>

          <CmsField
            label="Nombre"
            id="contactName"
            name="contactName"
            hint="Nombre de la persona que reserva"
            placeholder="Ej: Jorge Dubatti"
            value={contactName}
            error={nameError ?? undefined}
            requiredMark
            onChange={(event) => {
              setContactName(event.target.value);
              if (nameError) {
                setNameError(null);
              }
              setFormError(null);
            }}
            required
          />

          <CmsField
            label="WhatsApp"
            id="contactWhatsapp"
            name="contactWhatsapp"
            hint="Número con código de área, sin espacios ni guiones"
            placeholder="Ej: 3511234567"
            value={contactWhatsapp}
            error={whatsappError ?? undefined}
            requiredMark
            onChange={(event) => {
              setContactWhatsapp(event.target.value);
              if (whatsappError) {
                setWhatsappError(null);
              }
              setFormError(null);
            }}
            required
          />

          <CmsPageError error={formError} />

          <div className="calendar-booking-modal__honeypot" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              id="website"
              name="website"
              type="text"
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {TURNSTILE_SITE_KEY ? (
            <div className="calendar-booking-modal__turnstile">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                options={{ appearance: "interaction-only" }}
                onSuccess={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileToken(null)}
              />
            </div>
          ) : (
            <CmsPageError error="Falta configurar la verificación de seguridad (Turnstile)." />
          )}

          <div className="calendar-booking-modal__actions">
            <Button
              type="button"
              variant="secondary"
              appearance="linear"
              actionTrackSufix="tenant_calendar_booking_cancel"
              onClick={handleClose}
              disabled={step === "submitting"}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              actionTrackSufix="tenant_calendar_booking_submit"
              disabled={step === "submitting"}
            >
              {step === "submitting" ? "Enviando..." : "Enviar solicitud"}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default CalendarBookingModal;
